<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import httpClient from '@/utils/httpClient'

const { t } = useI18n()

// Coordinates come from the forecast metadata (Forecast.vue resolves
// them via the existing /api/forecast response). Taking them as props
// avoids a second round-trip through autocomplete and keeps this
// component a pure presentational view.
const props = defineProps<{
  latitude: number | null | undefined
  longitude: number | null | undefined
}>()

interface Attribution { text: string; url: string }
interface WebcamResult {
  provider: string
  title: string
  location_label: string
  distance_km: number | null
  image_url: string | null
  preview_url: string | null
  image_ttl_sec: number
  detail_url: string | null
  player_url: string | null
  last_updated_at: string | null
  age_min: number | null
  attribution: Attribution
}

// Re-fetch on a modest interval so the still stays within a few
// minutes of real time. Backend caches per ~1km bucket for 10 min so
// this doesn't blow up upstream call volume.
const REFRESH_MS = 4 * 60 * 1000

// Required by Windy Webcams API ToU "Mention us" clause: every displayed
// image must carry a visible credit link to windy.com. The card itself
// is not interactive — only this link is — so the linking obligation
// rides entirely on the attribution anchor below.
const WINDY_HOME_URL = 'https://windy.com'

const pick = ref<WebcamResult | null>(null)
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let refreshTimer: ReturnType<typeof setInterval> | null = null

const ageLabel = computed<string | null>(() => {
  const m = pick.value?.age_min
  if (typeof m !== 'number' || m < 0) return null
  if (m < 1) return t('webcam.ageJustNow')
  return t('webcam.ageMinAgo', [m])
})

async function fetchNearest() {
  const lat = Number(props.latitude)
  const lon = Number(props.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    pick.value = null
    return
  }
  try {
    const resp = await httpClient.get('/api/webcams/nearest', {
      params: { lat, lon },
    })
    const results = (resp.data?.results ?? []) as WebcamResult[]
    pick.value = results.find((r) => !!r.image_url) || null
  } catch {
    pick.value = null
  }
}

function scheduleFetch() {
  if (debounceTimer !== null) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    fetchNearest()
  }, 500)
}

function startRefreshLoop() {
  stopRefreshLoop()
  refreshTimer = setInterval(() => {
    fetchNearest()
  }, REFRESH_MS)
}

function stopRefreshLoop() {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

// Publish the dock's reserved vertical slot so App.vue's .content-wrapper
// can extend its padding-bottom past the chat-dock and leave room for
// the fixed-positioned webcam without scroll content hiding behind it.
// Only claimed while a webcam is actually rendered (pick has an image).
const WEBCAM_DOCK_HEIGHT = '92px'

function setDockHeightVar() {
  document.documentElement.style.setProperty('--webcam-dock-height', WEBCAM_DOCK_HEIGHT)
}

function clearDockHeightVar() {
  document.documentElement.style.removeProperty('--webcam-dock-height')
}

watch(
  () => pick.value?.image_url,
  (url) => {
    if (url) setDockHeightVar()
    else clearDockHeightVar()
  },
  { immediate: true },
)

onMounted(() => {
  fetchNearest()
  startRefreshLoop()
})

onBeforeUnmount(() => {
  stopRefreshLoop()
  if (debounceTimer !== null) clearTimeout(debounceTimer)
  clearDockHeightVar()
})

watch(
  () => [props.latitude, props.longitude],
  scheduleFetch,
)
</script>

<template>
  <div v-if="pick && pick.image_url" class="webcam-panel">
    <img
      :src="pick.image_url"
      :alt="pick.title"
      class="webcam-panel__thumb"
      loading="lazy"
    />
    <div class="webcam-panel__body">
      <div class="webcam-panel__title-row">
        <div class="webcam-panel__title" :title="pick.title">{{ pick.title }}</div>
        <span
          v-if="pick.distance_km != null"
          class="webcam-panel__distance"
        >{{ pick.distance_km }}&nbsp;km</span>
      </div>
      <div v-if="ageLabel" class="webcam-panel__meta">
        <span class="webcam-panel__age">{{ ageLabel }}</span>
      </div>
      <span class="webcam-panel__attr">
        Webcams provided by
        <a
          :href="WINDY_HOME_URL"
          target="_blank"
          rel="noopener noreferrer"
        >windy.com</a>
      </span>
    </div>
  </div>
</template>

<style scoped>
/* Fixed dock with the same raised-edge treatment as .chat-dock —
 * hairline border-top + upward ambient shadow. Sits immediately above
 * the chat-dock so the two read as a matching pair of pinned panels.
 * The card itself is NOT interactive — only the windy.com attribution
 * link is — so no cursor:pointer, no hover/focus shadow change. */
.webcam-panel {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  /* Sit flush against the chat-dock's top edge. --chat-dock-height is
   * published by ChatBar (ResizeObserver) and already includes the
   * dock's own safe-area padding. --chat-bottom-offset tracks the
   * iOS-keyboard push so webcam + dock rise together when the keyboard
   * opens. Fallback covers first paint before ChatBar mounts. */
  bottom: calc(var(--chat-bottom-offset, 0px) + var(--chat-dock-height, calc(56px + env(safe-area-inset-bottom))));
  width: 100%;
  max-width: var(--app-column-max-width, 640px);
  z-index: 7;
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 6px 12px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.1);
  text-align: left;
}

.webcam-panel__thumb {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.webcam-panel__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.webcam-panel__title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.webcam-panel__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.webcam-panel__distance {
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.7);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.webcam-panel__meta {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.webcam-panel__age {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.webcam-panel__attr {
  font-size: 0.72rem;
  line-height: 1.35;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.webcam-panel__attr a {
  color: inherit;
  text-decoration: none;
}

.webcam-panel__attr a:hover {
  text-decoration: underline;
}
</style>
